<div class="setup-agent-container">

  <!-- Header -->
  <div class="setup-header">
    <div class="setup-title">
      <i class="fa-solid fa-wand-magic-sparkles"></i>
      <h2>Knowledge Base Setup</h2>
    </div>
    <p class="setup-description">
      Generate your knowledge base by crawling your organization's website. The AI agent will analyze your site and produce all 8 configuration sections for review.
    </p>
  </div>

  <!-- Input Section -->
  <div class="input-section">
    <div class="input-row">
      <div class="input-group">
        <label for="websiteUrl">Website URL</label>
        <input
          id="websiteUrl"
          type="url"
          [(ngModel)]="websiteUrl"
          placeholder="https://www.example.org"
          [disabled]="isRunning"
          class="url-input"
        />
      </div>
      <div class="input-group">
        <label for="orgName">Organization Name</label>
        <input
          id="orgName"
          type="text"
          [(ngModel)]="organizationName"
          placeholder="Organization name"
          [disabled]="isRunning"
          class="name-input"
        />
      </div>
    </div>

    <div class="action-row">
      <button
        class="btn btn-primary generate-btn"
        (click)="runSetupAgent()"
        [disabled]="!canRun"
      >
        <i class="fa-solid" [ngClass]="isRunning ? 'fa-spinner fa-spin' : 'fa-wand-magic-sparkles'"></i>
        {{ isRunning ? 'Generating...' : 'Generate Knowledge Base' }}
      </button>

      <span *ngIf="lastRunAt" class="last-run">
        <i class="fa-solid fa-clock"></i>
        Last run: {{ formatDate(lastRunAt) }}
      </span>
    </div>
  </div>

  <!-- Pending Website Sync Banner -->
  <div *ngIf="hasPendingSync" class="pending-sync-banner">
    <div class="pending-sync-info">
      <i class="fa-solid fa-rotate"></i>
      <span>
        A scheduled website sync completed on {{ formatDate(pendingSyncDate) }} — review proposed changes before they are applied.
      </span>
    </div>
    <div class="pending-sync-actions">
      <button class="btn btn-sm btn-primary" (click)="reviewPendingSync()">
        <i class="fa-solid fa-magnifying-glass"></i>
        Review Changes
      </button>
      <button class="btn btn-sm btn-outline" (click)="dismissPendingSync()">
        Dismiss
      </button>
    </div>
  </div>

  <!-- Progress Message -->
  <div *ngIf="progressMessage" class="progress-banner">
    <i class="fa-solid fa-circle-info"></i>
    {{ progressMessage }}
  </div>

  <!-- Error Message -->
  <div *ngIf="errorMessage" class="error-banner">
    <i class="fa-solid fa-circle-exclamation"></i>
    {{ errorMessage }}
  </div>

  <!-- Results Section -->
  <div *ngIf="hasResults" class="results-section">

    <!-- Bulk Actions -->
    <div class="bulk-actions">
      <span class="results-summary">
        {{ acceptedCount }} of {{ sectionStates.length }} sections accepted
      </span>
      <div class="bulk-buttons">
        <button class="btn btn-sm btn-outline" (click)="toggleSelectAll()">
          {{ acceptedCount === sectionStates.length ? 'Deselect All' : 'Select All' }}
        </button>
        <button
          class="btn btn-sm btn-primary"
          (click)="saveAcceptedSections()"
          [disabled]="acceptedCount === 0"
        >
          <i class="fa-solid fa-floppy-disk"></i>
          Save {{ acceptedCount }} Section(s)
        </button>
      </div>
    </div>

    <!-- Crawl Summary -->
    <div *ngIf="crawlSummary" class="crawl-summary">
      <span class="crawl-stat">
        <i class="fa-solid fa-globe"></i>
        {{ crawlSummary.urlsFetched.length }} pages crawled
      </span>
      <span *ngIf="crawlSummary.urlsFailed.length > 0" class="crawl-stat crawl-warn">
        <i class="fa-solid fa-triangle-exclamation"></i>
        {{ crawlSummary.urlsFailed.length }} pages failed
      </span>
      <span *ngIf="crawlSummary.searchQueries.length > 0" class="crawl-stat">
        <i class="fa-solid fa-magnifying-glass"></i>
        {{ crawlSummary.searchQueries.length }} searches run
      </span>
    </div>

    <!-- Section Cards -->
    <div class="section-cards">
      <div
        *ngFor="let state of sectionStates; let i = index"
        class="section-card"
        [class.accepted]="state.accepted"
        [class.rejected]="!state.accepted"
      >
        <!-- Card Header -->
        <div class="card-header" (click)="toggleExpand(state)">
          <div class="card-header-left">
            <input
              type="checkbox"
              [checked]="state.accepted"
              (click)="$event.stopPropagation(); toggleSectionAccept(state)"
              class="accept-checkbox"
            />
            <span class="section-name">{{ state.section.settingName }}</span>
            <span class="confidence-badge" [ngClass]="getConfidenceBadgeClass(state.section.confidence)">
              {{ state.section.confidence }}
            </span>
          </div>
          <div class="card-header-right">
            <span class="setting-key">{{ state.section.settingKey }}</span>
            <i class="fa-solid" [ngClass]="state.isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
          </div>
        </div>

        <!-- Card Body (expanded) -->
        <div *ngIf="state.isExpanded" class="card-body">

          <!-- Confidence reason -->
          <div class="confidence-reason">
            <i class="fa-solid fa-info-circle"></i>
            {{ state.section.confidenceReason }}
          </div>

          <!-- Source URLs -->
          <div *ngIf="state.section.sourceUrls?.length > 0" class="sources">
            <button class="btn-link" (click)="$event.stopPropagation(); toggleSources(state)">
              <i class="fa-solid" [ngClass]="state.showSources ? 'fa-eye-slash' : 'fa-eye'"></i>
              {{ state.showSources ? 'Hide' : 'Show' }} sources ({{ state.section.sourceUrls.length }})
            </button>
            <ul *ngIf="state.showSources" class="source-list">
              <li *ngFor="let url of state.section.sourceUrls">{{ url }}</li>
            </ul>
          </div>

          <!-- Content Preview / Edit -->
          <div class="content-area">
            <div *ngIf="!state.isEditing" class="content-preview">
              <div class="markdown-rendered" [innerHTML]="state.renderedHtml"></div>
              <button class="btn btn-sm btn-outline edit-btn" (click)="startEditing(state)">
                <i class="fa-solid fa-pen"></i> Edit
              </button>
            </div>
            <div *ngIf="state.isEditing" class="content-editor">
              <textarea
                [(ngModel)]="state.editedValue"
                class="edit-textarea"
                rows="12"
              ></textarea>
              <div class="edit-actions">
                <button class="btn btn-sm btn-primary" (click)="applyEdit(state)">
                  <i class="fa-solid fa-check"></i> Apply
                </button>
                <button class="btn btn-sm btn-outline" (click)="cancelEditing(state)">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>

  <!-- Empty State -->
  <div *ngIf="!hasResults && !isRunning && !errorMessage" class="empty-state">
    <i class="fa-solid fa-wand-magic-sparkles empty-icon"></i>
    <p>Enter your organization's website URL and click Generate to create your knowledge base.</p>
  </div>

</div>
